import {
  TextField,
  Grid,
  FormControlLabel,
  IconButton,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';
import { createCustomerPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { isNotNullOrUndefined } from '@vy/utils';
import { vyFormRegister } from '../../../../../../utils/react-hook-forms-utils';
import { Controller, FieldArrayWithId, useFormContext } from 'react-hook-form';
import { VyIcon } from '@vy/ui-design-system';
import { NewCustomer } from '@vy/types';
import { UpdateCustomerWithExtendedContactsField } from '../../../../UpdateCustomerSettingsPage/update-customer-settings-hooks/types/update-customer-with-extended-contacts';
import { VyCheckbox } from '../../../../../../components/new-design/inputs/VyCheckbox';

type FormDataType = NewCustomer | UpdateCustomerWithExtendedContactsField;

type CustomerContractItemProps = {
  field: FieldArrayWithId<FormDataType, 'contacts', 'id'>;
  index: number;
  handlePhoneKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handlePhoneInput: (e: React.FormEvent<HTMLInputElement>) => void;
  remove: (id: string, index: number) => void;
  isManageModeEnabled: boolean;
  isSelected: boolean;
  handleContactSelectionToggle: () => void;
};

export const CustomerContractItem: React.FC<CustomerContractItemProps> = ({
  field,
  index,
  handlePhoneKeyDown,
  handlePhoneInput,
  handleContactSelectionToggle,
  remove,
  isManageModeEnabled,
  isSelected,
}) => {
  const { register, control, formState, trigger } = useFormContext<FormDataType>();

  return (
    <Grid container size={12} spacing={3}>
      <Grid size={12}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Typography variant="body1" sx={{
            fontWeight: 500
          }}>
            Contact {index + 1}
          </Typography>
        </Stack>
      </Grid>
      {isManageModeEnabled && (
        <Grid size={1}>
          <VyCheckbox
            checked={isSelected}
            onChange={handleContactSelectionToggle}
            labelWrapperSx={{ marginTop: '24px', marginLeft: 1 }}
          />
        </Grid>
      )}
      <Grid container size={isManageModeEnabled ? 11 : 12} rowSpacing={3} columnSpacing={2}>
        <Grid size={4}>
          <TextField
            {...getSelectorAttribute(createCustomerPageSelectors.companyDetails.contactName)}
            label="Contact Name"
            placeholder="e.g. John Doe"
            fullWidth
            defaultValue={field.name}
            error={isNotNullOrUndefined(formState.errors.contacts?.[index]?.name)}
            {...vyFormRegister(register, `contacts.${index}.name`)}
            helperText={formState.errors.contacts?.[index]?.name?.message}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            {...getSelectorAttribute(createCustomerPageSelectors.companyDetails.contactTitle)}
            label="Contact Title"
            placeholder="e.g. Manager"
            fullWidth
            defaultValue={field.title}
            error={isNotNullOrUndefined(formState.errors.contacts?.[index]?.title)}
            {...vyFormRegister(register, `contacts.${index}.title`)}
            helperText={formState.errors.contacts?.[index]?.title?.message}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            {...getSelectorAttribute(createCustomerPageSelectors.companyDetails.contactPhone)}
            label="Phone No."
            placeholder="e.g +123456789"
            fullWidth
            defaultValue={field.phone}
            error={isNotNullOrUndefined(formState.errors.contacts?.[index]?.phone)}
            {...vyFormRegister(register, `contacts.${index}.phone`)}
            helperText={formState.errors.contacts?.[index]?.phone?.message}
            slotProps={{
              htmlInput: {
                onKeyDown: handlePhoneKeyDown,
                onInput: handlePhoneInput,
              },
            }}
            onBlur={() => trigger(`contacts.${index}.phone`)}
          />
        </Grid>

        <Grid size={4}>
          <TextField
            {...getSelectorAttribute(createCustomerPageSelectors.companyDetails.contactEmail)}
            label="Contact Email"
            placeholder="e.g. johndoe@example.com"
            fullWidth
            defaultValue={field.email}
            {...vyFormRegister(register, `contacts.${index}.email`)}
            error={isNotNullOrUndefined(formState.errors.contacts?.[index]?.email)}
            helperText={formState.errors.contacts?.[index]?.email?.message}
          />
        </Grid>
        <Grid size={4} sx={{
          display: "flex"
        }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              marginTop: 'auto',
              marginBottom: 0.5
            }}>
            <Controller
              control={control}
              name={`contacts.${index}.receiveInvoiceEmail`}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label="Send Invoice"
                  sx={{ margin: 0 }}
                />
              )}
            />
            <IconButton onClick={() => remove(field.id, index)} sx={{ padding: 1 }}>
              <VyIcon icon="Truncate" />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  );
};
